import React from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import uuidV4 from 'uuid/v4';

import Helmet from 'wanderu-component-helmet';
import { fetchAllCarriersAction } from 'wanderu-ducks-carriers';
import {
    fetchCachedTripsAction,
    fetchTripsAction,
    searchActions,
    searchSelectors,
} from 'wanderu-ducks-search';
import { localize, selectLocale } from 'wanderu-i18n';
import { SnowplowService } from 'wanderu-service-analytics';
import SearchWidget from 'wanderu-widget';

import Search from '../../components/Search/Search';
import searchConfig from '../../config/search.locale';
import searchUrlManager from '../../hocs/searchUrlManager/searchUrlManager';

class SearchContainer extends React.Component {
    static async serverFetchData(opts) {
        const {
            dispatch,
            getState,
            params,
            query,
        } = opts;

        const urlProps = {
            location: { search: query },
            match: { params },
        };
        const searchParamsAreValid = searchSelectors.selectSearchParamsAreValid(
            getState(),
            urlProps,
        );
        if (!searchParamsAreValid) {
            return;
        }

        const searchParams = searchSelectors.selectReduxSearchParams(getState());
        const batchId = uuidV4();
        SnowplowService.setBatchId(batchId);
        let promises = [
            dispatch(fetchAllCarriersAction.fetch()),
            dispatch(fetchCachedTripsAction.fetch(searchParams, batchId, urlProps)),
            SearchWidget.serverFetchData(opts),
        ];

        // TODO: Have tripsummary be responsible for loading its own data, search
        //      doesn't need this data
        if (searchParams.departTripId) {
            promises.push(dispatch(fetchTripsAction.fetchTrip(searchParams.departTripId)));
        }

        await Promise.all(promises);
    }

    componentDidMount() {
        if (this.shouldFetchAllCarriers()) {
            this.props.fetchAllCarriers();
        }
    }

    shouldFetchAllCarriers() {
        const {
            carriersError,
            carriersFetching,
            carriersSuccess,
        } = this.props;

        if (carriersError || carriersFetching) {
            return false;
        }

        return !carriersSuccess;
    }

    onOriginChange = (input, placeId) => {
        this.props.setOriginUserInputForPlaceId(input, placeId);
    };

    onDestinationChange = (input, placeId) => {
        this.props.setDestinationUserInputForPlaceId(input, placeId);
    };

    onOriginInputChange = (input) => {
        this.props.setOriginUserInput(input);
    };

    onDestinationInputChange = (input) => {
        this.props.setDestinationUserInput(input);
    };

    render() {
        const {
            arriveCity,
            departCity,
            userOriginInput,
            userDestinationInput,
            userDestinationPlaceId,
            userOriginPlaceId,
            searchParams,
            route,
            t,
        } = this.props;

        const description = t('simplest-way-to-book-search-carriers-no-fees~v1', {
            arriveCity,
            departCity,
        });
        const title = t('search-cheap-bus-and-train-tickets-wanderu~v1', {
            arriveCity,
            departCity,
        });

        return (
            <>
                <Helmet
                    description={description}
                    supportedLocales={searchConfig.supportedLocales}
                    title={title}
                    noFollow
                    noIndex
                />
                <Search
                    departDate={searchParams.departDate}
                    destinationPlaceId={userDestinationPlaceId}
                    destinationText={userDestinationInput}
                    direction={searchParams.direction}
                    location={this.props.location}
                    onDestinationChange={this.onDestinationChange}
                    onDestinationInputChange={this.onDestinationInputChange}
                    onOriginChange={this.onOriginChange}
                    onOriginInputChange={this.onOriginInputChange}
                    originPlaceId={userOriginPlaceId}
                    originText={userOriginInput}
                    returnDate={searchParams.returnDate}
                />
                {renderRoutes(route.routes)}
            </>
        );
    }
}

const mapDispatchToProps = {
    fetchAllCarriers: fetchAllCarriersAction.fetch,
    setDestinationUserInput: searchActions.setDestinationUserInput,
    setDestinationUserInputForPlaceId: searchActions.setDestinationUserInputForPlaceId,
    setOriginUserInput: searchActions.setOriginUserInput,
    setOriginUserInputForPlaceId: searchActions.setOriginUserInputForPlaceId,
};

function mapStateToProps(state, ownProps) {
    const searchParams = searchSelectors.selectSearchParams(state, ownProps);
    const {
        originPlaceId,
        destinationPlaceId,
    } = searchParams;

    return {
        arriveCity: searchSelectors.selectDestinationCityName(state),
        carriersError: fetchAllCarriersAction.selectError(state),
        carriersFetching: fetchAllCarriersAction.selectWorking(state),
        carriersSuccess: fetchAllCarriersAction.selectSuccess(state),
        departCity: searchSelectors.selectOriginCityName(state),
        locale: selectLocale(state),
        searchParams,
        userDestinationInput: searchSelectors.selectDestinationUserInput(state),
        userDestinationPlaceId: searchSelectors.selectDestinationUserPlaceId(
            state,
            destinationPlaceId,
        ),
        userOriginInput: searchSelectors.selectOriginUserInput(state),
        userOriginPlaceId: searchSelectors.selectOriginUserPlaceId(
            state,
            originPlaceId,
        ),
    };
}

const routePropType = {
    component: PropTypes.elementType,
    key: PropTypes.string,
    path: PropTypes.string,
};

SearchContainer.propTypes = {
    arriveCity: PropTypes.string,
    carriersError: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    carriersFetching: PropTypes.bool,
    carriersSuccess: PropTypes.bool,
    departCity: PropTypes.string,
    fetchAllCarriers: PropTypes.func,
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
    route: PropTypes.shape({
        ...routePropType,
        routes: PropTypes.arrayOf(PropTypes.shape(routePropType)),
    }),
    searchParams: PropTypes.shape({
        departDate: PropTypes.string,
        destinationPlaceId: PropTypes.string,
        direction: PropTypes.string,
        originPlaceId: PropTypes.string,
        returnDate: PropTypes.string,
    }),
    setDestinationUserInput: PropTypes.func,
    setDestinationUserInputForPlaceId: PropTypes.func,
    setOriginUserInput: PropTypes.func,
    setOriginUserInputForPlaceId: PropTypes.func,
    t: PropTypes.func,
    userDestinationInput: PropTypes.string,
    userDestinationPlaceId: PropTypes.string,
    userOriginInput: PropTypes.string,
    userOriginPlaceId: PropTypes.string,
};

export {
    SearchContainer,
    mapStateToProps,
};
export default searchUrlManager(
    connect(mapStateToProps, mapDispatchToProps)(localize(SearchContainer, 'baseApp')),
);
